import { isMobile } from '@farmasiet/frontendcore-reactcomponents';
import * as React from 'react';

interface TouchSwiperProps {
    readonly children: React.ReactNode;
    readonly onActiveItemsChange?: (firstIndex: number, lastIndex: number) => void;
    readonly lastVisibleElementPadding?: number;
    readonly onlyOnMobile?: boolean;
}

interface TouchSwiperState {
    readonly leftPosition: number;
    readonly touchInitialPositionX: number;
    readonly touchCurrentPositionX: number;
    readonly touchCurrentPositionY: number;
    readonly swipeEnabled: boolean;
    readonly elementsContainerWidth: number;
    readonly scrollLock: boolean;
    readonly swipeLock: boolean;
}

class TouchSwiper extends React.Component<TouchSwiperProps, TouchSwiperState> {
    state = {
        leftPosition: 0,
        touchInitialPositionX: 0,
        touchCurrentPositionX: 0,
        touchCurrentPositionY: 0,
        swipeEnabled: false,
        elementsContainerWidth: 0,
        scrollLock: false,
        swipeLock: false
    };

    breakpoints = [0];
    elementsWidth: number[] = [];
    containerRef = React.createRef<HTMLUListElement>();
    wrapperRef = React.createRef<HTMLDivElement>();
    screenWidth = -1;

    componentDidMount = () => {
        this.initElementsArrays();

        if (this.props.onActiveItemsChange) this.props.onActiveItemsChange(0, this.getLastVisibleElementIndex(0));

        window.addEventListener('touchmove', this.lockScrollingVertically, {
            passive: false
        });
        window.addEventListener('resize', this.handleResize);
        window.addEventListener('load', this.initElementsArrays);
        this.screenWidth = window.innerWidth;
    };

    componentWillUnmount = () => {
        window.removeEventListener('touchmove', this.lockScrollingVertically);
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('load', this.initElementsArrays);
    };

    componentDidUpdate(prevProps: TouchSwiperProps) {
        if (React.Children.count(prevProps.children) !== React.Children.count(this.props.children)) {
            this.initElementsArrays();
        }
    }

    private lockScrollingVertically = (e: TouchEvent) => {
        if (this.state.scrollLock) {
            e.preventDefault();
        }
    };

    private initElementsArrays = () => {
        if (this.containerRef.current) {
            const childrenLength = this.containerRef.current.children.length;
            this.breakpoints = [0];
            this.elementsWidth = [];

            for (let i = 0; i < childrenLength; i++) {
                const width = this.getElementWidth(this.containerRef.current.children[i] as HTMLElement);
                this.breakpoints.push(width + this.breakpoints[this.breakpoints.length - 1]);
                this.elementsWidth.push(width);
            }

            this.adjustBreakpoints();

            this.setState({
                elementsContainerWidth: this.containerRef.current.offsetWidth
            });
        }
    };

    private getElementWidth = (element: HTMLElement): number => {
        const width = element.offsetWidth;
        const styles = window.getComputedStyle(element);
        const marginLeft = parseFloat(styles.marginLeft) || 0;
        const marginRight = parseFloat(styles.marginRight) || 0;
        return marginLeft + width + marginRight;
    };

    private adjustBreakpoints = () => {
        const elementsTotalWidth = this.getElementsTotalWidth();
        const wrapperWidth = this.getWrapperWidth();

        if (elementsTotalWidth > wrapperWidth) {
            const lastBreakpoint = elementsTotalWidth - wrapperWidth;
            this.breakpoints = this.breakpoints.filter((x) => x < lastBreakpoint);
            this.breakpoints.push(lastBreakpoint);
        } else {
            this.breakpoints = [];
        }
    };

    private handleResize = () => {
        if (window.innerWidth !== this.screenWidth) {
            this.screenWidth = window.innerWidth;
            this.setState({ leftPosition: 0 });
            this.initElementsArrays();

            if (this.props.onActiveItemsChange) this.props.onActiveItemsChange(0, this.getLastVisibleElementIndex(0));
        }
    };

    private getClosestBreakpoint = (): number => {
        let closestBreakpoint = 0;
        let closestDistance: number | null = null;

        this.breakpoints.forEach((breakpoint) => {
            const distance = Math.abs(-breakpoint - this.state.leftPosition);

            if (closestDistance === null) {
                closestBreakpoint = breakpoint;
                closestDistance = distance;
            } else {
                if (distance < closestDistance) {
                    closestBreakpoint = breakpoint;
                    closestDistance = distance;
                }
            }
        });

        return closestBreakpoint;
    };

    private getLastVisibleElementIndex = (startIndex: number): number => {
        const swiperWidth = this.getWrapperWidth();
        let full = false;
        let lastIndex = startIndex;
        let totalWidth = this.elementsWidth[startIndex];
        const padding = this.getLastVisibleElementPadding();

        for (let i = startIndex; i < this.elementsWidth.length; i++) {
            if (totalWidth + this.elementsWidth[i] - padding <= swiperWidth && !full) {
                lastIndex++;
                totalWidth += this.elementsWidth[i];
            } else {
                full = true;
            }
        }

        return lastIndex || startIndex;
    };

    private getLastVisibleElementPadding = (): number => {
        if (this.props.lastVisibleElementPadding) return this.props.lastVisibleElementPadding;
        else return 0;
    };

    private getElementsTotalWidth = (): number => {
        return this.elementsWidth.reduce((a, b) => a + b, 0);
    };

    private updatePosition = (difference: number, clientX: number) => {
        const closestBreakpointIndex = this.breakpoints.indexOf(this.getClosestBreakpoint());

        this.setState((prevState) => ({
            leftPosition: prevState.leftPosition - difference,
            touchCurrentPositionX: clientX
        }));

        if (this.props.onActiveItemsChange) this.props.onActiveItemsChange(closestBreakpointIndex, this.getLastVisibleElementIndex(closestBreakpointIndex));
    };

    private handleTouchMove = (e: React.TouchEvent) => {
        const { clientX, clientY } = e.touches && e.touches[0] ? e.touches[0] : { clientX: 0, clientY: 0 };
        const scrollLockOffset = 6;

        if (this.state.swipeEnabled) {
            const differenceX = this.state.touchCurrentPositionX - clientX;
            const differenceY = this.state.touchCurrentPositionY - clientY;

            if (!this.state.swipeLock && !this.state.scrollLock) {
                if (Math.abs(differenceX) > scrollLockOffset) {
                    this.setState({ scrollLock: true });
                } else if (Math.abs(differenceY) > scrollLockOffset) {
                    this.setState({ swipeLock: true });
                }
            }

            const lastBreakpointIndex = this.breakpoints.length - 1;
            const lastBreakpoint = -this.breakpoints[lastBreakpointIndex];

            if (this.state.leftPosition - differenceX < 0 && this.state.leftPosition - differenceX > lastBreakpoint && !this.state.swipeLock) {
                this.updatePosition(differenceX, clientX);
            }
        }
    };

    private handleTouchStart = (e: React.TouchEvent) => {
        if ((this.props.onlyOnMobile ? isMobile() : true) && e.touches && e.touches[0]) {
            this.setState({
                touchInitialPositionX: e.touches[0].clientX,
                touchCurrentPositionX: e.touches[0].clientX,
                touchCurrentPositionY: e.touches[0].clientY,
                swipeEnabled: true
            });
        }
    };

    private handleTouchEnd = () => {
        const closestBreakpoint = -this.getClosestBreakpoint();
        const closestBreakpointIndex = this.breakpoints.indexOf(-closestBreakpoint);
        const direction = this.state.touchInitialPositionX - this.state.touchCurrentPositionX;
        let snapToBreakpoint = 0;

        if (direction > 0) {
            // swiping right
            if (closestBreakpoint < this.state.leftPosition || this.state.swipeLock) {
                snapToBreakpoint = closestBreakpoint;
            } else if (!this.state.swipeLock) {
                snapToBreakpoint =
                    this.breakpoints[closestBreakpointIndex + 1] !== undefined ? -this.breakpoints[closestBreakpointIndex + 1] : closestBreakpoint;
            }
        } else {
            // swiping left
            if (closestBreakpoint < this.state.leftPosition && !this.state.swipeLock) {
                snapToBreakpoint =
                    this.breakpoints[closestBreakpointIndex - 1] !== undefined ? -this.breakpoints[closestBreakpointIndex - 1] : closestBreakpoint;
            } else {
                snapToBreakpoint = closestBreakpoint;
            }
        }

        const snapToBreakpointIndex = this.breakpoints.indexOf(-snapToBreakpoint);

        if (this.props.onActiveItemsChange) this.props.onActiveItemsChange(snapToBreakpoint, this.getLastVisibleElementIndex(snapToBreakpointIndex));

        this.setState({
            swipeEnabled: false,
            leftPosition: snapToBreakpoint,
            touchInitialPositionX: 0,
            touchCurrentPositionX: 0,
            touchCurrentPositionY: 0,
            scrollLock: false,
            swipeLock: false
        });
    };

    private getWrapperWidth = (): number => {
        return this.wrapperRef.current ? this.wrapperRef.current.offsetWidth + 1 : 0;
    };

    render() {
        const { children } = this.props;
        const { swipeEnabled, leftPosition } = this.state;

        return (
            <>
                <div
                    ref={this.wrapperRef}
                    className="TouchSwiper"
                    onTouchStart={this.handleTouchStart}
                    onTouchEnd={this.handleTouchEnd}
                    onTouchMove={this.handleTouchMove}
                >
                    <ul
                        ref={this.containerRef}
                        className={swipeEnabled ? 'TouchSwiper__content' : 'TouchSwiper__content TouchSwiper--animated'}
                        style={{ transform: `translateX(${leftPosition}px)` }}
                    >
                        {children}
                    </ul>
                </div>
            </>
        );
    }
}

export default TouchSwiper;
